import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api, API_BASE } from '../../lib/api';
import { Button } from '../ui/button';
import {
  ChatCircleDots, X, PaperPlaneRight, Sparkle, ArrowRight,
} from '@phosphor-icons/react';

/** Hikari Helper — floating context-aware chat. Mounted globally.
 *
 *  - On `/candidate/*` routes (authed): uses /api/assistant/chat with full candidate context.
 *  - On `/sandbox` (public): uses /api/sandbox/assistant/chat — no token required,
 *    employer-prospect persona, conversion nudges.
 */
export const HikariHelper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const path = location.pathname || '';
  const isSandbox = path.startsWith('/sandbox');
  const isCandidateRoute = path.startsWith('/candidate');

  const greeting = isSandbox
    ? "Hi — I'm the Hikari Helper. Ask me anything about how this ranking works. I'll keep it short."
    : "Hi — I'm the Hikari Helper. Ask me what to do next, why a score moved, or anything about the app.";

  const [history, setHistory] = useState([
    { role: 'assistant', text: greeting, next_step: null },
  ]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  // Reset the conversation if the user crosses between sandbox/candidate
  useEffect(() => {
    setHistory([{ role: 'assistant', text: greeting, next_step: null }]);
    setInput('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSandbox, isCandidateRoute]);

  const hasToken = typeof window !== 'undefined' && localStorage.getItem('hikari_token');

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, open]);

  // Honour sessionStorage handoffs (e.g. from intake's "Skip the questions" exit).
  // Only relevant inside the authenticated candidate area.
  useEffect(() => {
    if (!hasToken || !isCandidateRoute) return;
    let prompt = '';
    try { prompt = sessionStorage.getItem('hikari_helper_autoprompt') || ''; } catch { /* blocked */ }
    if (!prompt) return;
    try { sessionStorage.removeItem('hikari_helper_autoprompt'); } catch { /* blocked */ }
    setOpen(true);
    setTimeout(() => {
      setInput(prompt);
      setTimeout(() => {
        const sendBtn = document.querySelector('[data-testid="helper-send"]');
        if (sendBtn && !sendBtn.disabled) sendBtn.click();
      }, 250);
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Visibility rule:
  //   sandbox  -> always show (public)
  //   candidate -> show only if token present
  //   anywhere else -> hide
  if (!isSandbox && !(isCandidateRoute && hasToken)) return null;

  const send = async () => {
    const msg = input.trim();
    if (!msg || busy) return;
    setBusy(true);
    const nextHistory = [...history, { role: 'user', text: msg }];
    setHistory(nextHistory);
    setInput('');
    try {
      const historyPayload = nextHistory.map((h) => ({ role: h.role, text: h.text }));
      let resp;
      if (isSandbox) {
        // Sandbox visitors are unauthenticated; use plain axios + read the active
        // candidate id off the URL hash (set by /sandbox when the user clicks a row).
        let active = '';
        try { active = window.__hikariSandboxActiveCandidate || ''; } catch { /* noop */ }
        resp = await axios.post(`${API_BASE}/sandbox/assistant/chat`, {
          message: msg,
          history: historyPayload,
          active_candidate_id: active || null,
        });
      } else {
        resp = await api.post('/assistant/chat', {
          message: msg,
          history: historyPayload,
        });
      }
      setHistory([
        ...nextHistory,
        {
          role: 'assistant',
          text: resp.data.reply || '...',
          next_step: resp.data.next_step || null,
        },
      ]);
    } catch (e) {
      setHistory([
        ...nextHistory,
        {
          role: 'assistant',
          text: "I'm offline right now. Have a look around while I'm back.",
          next_step: isSandbox
            ? { label: 'See pricing', href: '/pricing' }
            : { label: 'Open dashboard', href: '/candidate' },
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const goNext = (href) => {
    setOpen(false);
    navigate(href);
  };

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-4 right-52 z-[70] flex items-center gap-2 bg-primary text-primary-foreground px-4 py-3 shadow-lg hover:bg-primary/90 transition-colors"
          data-testid="helper-fab"
        >
          <Sparkle size={18} weight="fill" />
          <span className="label-eyebrow">HELPER</span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-20 right-4 z-[75] w-[380px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] bg-white border border-border shadow-2xl flex flex-col"
          role="dialog"
          aria-label="Hikari Helper"
          data-testid="helper-panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border bg-foreground text-background">
            <div className="flex items-center gap-2">
              <Sparkle size={16} weight="fill" className="text-accent" />
              <div>
                <div className="label-eyebrow text-accent">HIKARI HELPER</div>
                <div className="mono text-[11px] opacity-70">
                  knows your profile · knows the app
                </div>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="opacity-70 hover:opacity-100"
              aria-label="Close helper"
              data-testid="helper-close"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-secondary/20"
            data-testid="helper-messages"
          >
            {history.map((m, i) => (
              <div
                key={i}
                className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-3 text-sm leading-relaxed ${
                    m.role === 'user'
                      ? 'bg-foreground text-background'
                      : 'bg-white border border-border'
                  }`}
                  data-testid={`helper-msg-${m.role}-${i}`}
                >
                  {m.text}
                  {m.next_step && m.role === 'assistant' && (
                    <button
                      onClick={() => goNext(m.next_step.href)}
                      className="mt-2 flex items-center gap-1 text-primary hover:underline label-eyebrow"
                      data-testid={`helper-next-${i}`}
                    >
                      {m.next_step.label} <ArrowRight size={11} />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start" data-testid="helper-typing">
                <div className="bg-white border border-border p-3 text-sm">
                  <span className="inline-block w-2 h-2 bg-foreground rounded-full animate-pulse mr-1" />
                  <span className="inline-block w-2 h-2 bg-foreground rounded-full animate-pulse mr-1 [animation-delay:150ms]" />
                  <span className="inline-block w-2 h-2 bg-foreground rounded-full animate-pulse [animation-delay:300ms]" />
                </div>
              </div>
            )}
          </div>

          {/* Quick suggestions (only on first turn) */}
          {history.length === 1 && (
            <div className="px-4 pb-2 flex flex-wrap gap-1.5" data-testid="helper-suggestions">
              {(isSandbox
                ? [
                    'Why is this candidate 92?',
                    'How is the fit score built?',
                    "What's pricing like?",
                  ]
                : [
                    'What should I do next?',
                    'How is my fit score built?',
                    'Help me build my profile',
                  ]
              ).map((s) => (
                <button
                  key={s}
                  onClick={() => setInput(s)}
                  className="label-eyebrow bg-secondary px-2 py-1 hover:bg-foreground hover:text-background"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {/* Composer */}
          <div className="flex items-center gap-2 p-3 border-t border-border bg-white">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask anything…"
              className="flex-1 h-10 px-3 border border-border text-sm rounded-none focus:outline-none focus:border-foreground"
              data-testid="helper-input"
              disabled={busy}
            />
            <Button
              onClick={send}
              disabled={busy || !input.trim()}
              className="rounded-none h-10 px-3"
              data-testid="helper-send"
              aria-label="Send"
            >
              <PaperPlaneRight size={14} />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default HikariHelper;
